/**
 * Game modes as data: a ModeConfig is a declarative ruleset carried inside
 * GameSettings. Engine code reads individual config values — it never asks
 * "is this gunbound?". Presets are defaults only: picking a mode prefills
 * the lobby settings, and the host can edit every field afterwards.
 */

export type ModeId = 'classic' | 'gunbound';

export interface AimConfig {
  /** Degrees per aim step (aim always moves in whole-degree increments). */
  stepDegrees: number;
  /** Allowed aim range in degrees (relative to horizontal, -90..90). */
  minAngle: number;
  maxAngle: number;
  /** Full-damage arc; shots outside it are damage-penalized. null = no penalty. */
  trueAngle: { min: number; max: number } | null;
  /** Damage multiplier for shots fired outside the true angle arc. */
  falseAngleDamageMultiplier: number;
  /** GunBound: aim rotates with the ground slope under the tank (the span is
   *  relative to the surface normal, not absolute vertical). Defaults off. */
  relativeToGround?: boolean;
}

export interface WindConfig {
  /** horizontal: classic left/right wind. vector360: any direction. */
  model: 'horizontal' | 'vector360';
  maxStrength: number;
}

export interface ModeWormConfig {
  /** Horizontal pixels a worm may walk per turn. null = unlimited. */
  movementBudgetPx: number | null;
  /** When true, a worm can no longer walk once it has fired this turn
   *  (GunBound rule). Defaults to false (classic: walk freely after firing). */
  lockMovementAfterFire?: boolean;
}

export interface FireConfig {
  /** hold-charge: classic space-hold. drag: GunBound-style drag shot. */
  input: 'hold-charge' | 'drag';
  /** Scales projectile launch speed derived from power. */
  maxPowerMultiplier: number;
  /** Charge accumulation in power units per second. */
  chargeRate: number;
  /** Global scale on the knockback an explosion applies to worms (on top of
   *  each shot's own push multiplier). 1 = classic Worms knockback; GunBound
   *  tones it down so shots barely shove tanks. Defaults to 1 when omitted. */
  explosionPushScale?: number;
}

export interface TurnOrderConfig {
  kind: 'roundRobin' | 'delay';
  /** Delay added per second spent during a turn (delay mode only). */
  delayPerSecond: number;
  /** Accumulate and display turn delays even when kind is roundRobin. */
  trackDelays: boolean;
}

export interface ItemsConfig {
  /** Weapon ids allowed in this match. null = all weapons. */
  whitelist: string[] | null;
  /** In-match consumable items and per-team counts (Phase 7). */
  matchItems: Record<string, number>;
}

export interface WeatherConfig {
  enabled: boolean;
  /** Enabled weather event types (Phase 8). */
  types: string[];
  /** Turns between weather changes (announce -> activate cadence). */
  intervalTurns: number;
}

export interface ModeConfig {
  id: ModeId;
  aim: AimConfig;
  wind: WindConfig;
  worm: ModeWormConfig;
  fire: FireConfig;
  turnOrder: TurnOrderConfig;
  items: ItemsConfig;
  weather: WeatherConfig;
  /** GunBound: teams pick a tank/mobile type (see core/tankTypes.ts) whose
   *  weapon preset + stats are authoritative (replaces items.whitelist).
   *  When false/absent (classic), all worms share the full DEFAULT_AMMO and
   *  there is no tank selection. */
  tankSelection?: boolean;
  /** When true, each team is assigned a RANDOM GunBound tank (grub/aduka/nak/
   *  lightning/frog — never worm or wizard) just before the game starts,
   *  overriding any per-slot lobby pick. Implies tank presets apply. */
  randomTank?: boolean;
}

/** Reproduces today's behavior exactly. */
export const CLASSIC_MODE: ModeConfig = {
  id: 'classic',
  aim: {
    stepDegrees: 1,
    minAngle: -90,
    maxAngle: 90,
    trueAngle: null,
    falseAngleDamageMultiplier: 1,
  },
  wind: { model: 'horizontal', maxStrength: 10 },
  worm: { movementBudgetPx: null },
  fire: { input: 'hold-charge', maxPowerMultiplier: 1, chargeRate: 100, explosionPushScale: 1 },
  turnOrder: { kind: 'roundRobin', delayPerSecond: 10, trackDelays: false },
  items: { whitelist: null, matchItems: {} },
  weather: { enabled: false, types: [], intervalTurns: 2 },
};

/** GunBound Thor's Hammer cross-breed defaults. */
export const GUNBOUND_MODE: ModeConfig = {
  id: 'gunbound',
  aim: {
    stepDegrees: 1,
    minAngle: 10,
    maxAngle: 80,
    trueAngle: { min: 40, max: 80 },
    falseAngleDamageMultiplier: 0.75,
    relativeToGround: true,
  },
  wind: { model: 'vector360', maxStrength: 15 },
  // Movement budget temporarily disabled (null = unlimited). Once you fire,
  // though, your turn's walking is locked.
  worm: { movementBudgetPx: null, lockMovementAfterFire: true },
  // 1.4x launch speed. Range scales with speed squared, so this puts a
  // full-power grenade/mortar (linear 750px/s curve) at roughly one map
  // width on flat ground; tailwind can stretch it past, headwind under.
  // Knockback toned right down for GunBound - shots barely shove tanks.
  fire: { input: 'drag', maxPowerMultiplier: 1.4, chargeRate: 100, explosionPushScale: 0.25 },
  // Delay-based ordering exists but is disabled for now: delays are still
  // tracked and displayed, turn order stays round-robin.
  turnOrder: { kind: 'roundRobin', delayPerSecond: 10, trackDelays: true },
  // Weapons are now defined by each team's TANK TYPE preset (see
  // core/tankTypes.ts), which is authoritative - the old per-mode whitelist is
  // gone. The worm tank's preset reproduces the previous GunBound kit exactly.
  items: {
    whitelist: null,
    // Blood and Energy-Up exist but are off by default (lobby-configurable).
    matchItems: { dual: 1, powerUp: 2, changeWind: 1 },
  },
  weather: { enabled: true, types: ['windChange', 'force', 'tornado', 'thor'], intervalTurns: 2 },
  tankSelection: true,
};

export const MODE_PRESETS: Record<ModeId, ModeConfig> = {
  classic: CLASSIC_MODE,
  gunbound: GUNBOUND_MODE,
};

/** Deep-merge a partial override onto a base mode config (per section). */
export function mergeModeConfig(base: ModeConfig, override?: Partial<ModeConfig>): ModeConfig {
  if (!override) return cloneModeConfig(base);
  return {
    id: override.id ?? base.id,
    aim: { ...base.aim, ...override.aim },
    wind: { ...base.wind, ...override.wind },
    worm: { ...base.worm, ...override.worm },
    fire: { ...base.fire, ...override.fire },
    turnOrder: { ...base.turnOrder, ...override.turnOrder },
    items: { ...base.items, ...override.items },
    weather: { ...base.weather, ...override.weather },
    tankSelection: override.tankSelection ?? base.tankSelection,
    randomTank: override.randomTank ?? base.randomTank,
  };
}

export function cloneModeConfig(mode: ModeConfig): ModeConfig {
  return {
    id: mode.id,
    aim: { ...mode.aim, trueAngle: mode.aim.trueAngle ? { ...mode.aim.trueAngle } : null },
    wind: { ...mode.wind },
    worm: { ...mode.worm },
    fire: { ...mode.fire },
    turnOrder: { ...mode.turnOrder },
    items: {
      whitelist: mode.items.whitelist ? [...mode.items.whitelist] : null,
      matchItems: { ...mode.items.matchItems },
    },
    weather: {
      enabled: mode.weather.enabled,
      types: [...mode.weather.types],
      intervalTurns: mode.weather.intervalTurns,
    },
    tankSelection: mode.tankSelection,
    randomTank: mode.randomTank,
  };
}
